'use client';

import { useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { AppButton } from './AppButton';
import { AppInput } from './AppInput';

export interface ChipInputProps {
  value: string[];
  onChange: (val: string[]) => void;
  placeholder: string;
  prefix?: string;
}

/**
 * 칩(태그) 입력 컴포넌트
 * 해시태그나 태그를 칩 형태로 추가/삭제합니다.
 */
export function ChipInput({
  value = [],
  onChange,
  placeholder,
  prefix = '',
}: ChipInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }
    const newValue = prefix && !trimmed.startsWith(prefix) ? `${prefix}${trimmed}` : trimmed;
    if (!value.includes(newValue)) {
      onChange([...value, newValue]);
    }
    setInputValue('');
  }, [inputValue, prefix, value, onChange]);

  const handleRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((item, idx) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 text-neutral-700 text-xs rounded-full"
          >
            {item}
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              aria-label={`${item} 삭제`}
              className="text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <AppInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
        />
        <AppButton type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="w-3 h-3 mr-1" />
          추가
        </AppButton>
      </div>
    </div>
  );
}
